import { useState } from "react";
import {
    Button, Typography,
    Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useStudents } from "../../context/students";
import axiosManager from "../../services/axiosManager";
import FileIcon from "../../assets/images/icons/session-note.svg?react";
import DownloadIcon from "../../assets/images/icons/download-session-note.svg?react";
import { formatTo } from "../../utils/dateTimeHelper";
import "./SessionNote.css";

const SessionNote = ({ sessionNote }) => {
    const { t } = useTranslation();
    const { activeStudentId } = useStudents();

    const [isDownloading, setIsDownloading] = useState(false);

    const sessionDate = new Date(sessionNote.sessionDate);

    const { mutateAsync: getSessionNotePdf } = useMutation({
        mutationFn: async () => {
            const response = await axiosManager.request$(`/parent/api/students/${activeStudentId}/mentorpathway/sessionnotes/${sessionNote.id}/pdf`, "GET", null, { responseType: "blob" });

            return response.data;
        },
    });

    const handleDownload = async (event) => {
        event.stopPropagation();
        setIsDownloading(true);

        try {
            const pdf = await getSessionNotePdf();
            const url = URL.createObjectURL(pdf);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Session_Note_${formatTo(sessionDate, "dd_MMM_yyyy")}.pdf`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="session-note">
            <div className="session-note__icon-container">
                <FileIcon className="session-note__icon" />
            </div>
            <div className="session-note__main-container">
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${sessionNote.id}-panel-content`}
                        id={`${sessionNote.id}-panel-header`}
                    >
                        <Typography variant="h4" className="text-primary">{formatTo(sessionDate, "dd MMM yyyy")}</Typography>
                        <Typography variant="h5" className="text-primary">{t("sessionNotes.sessionFocus")}</Typography>
                        <Typography variant="body1">{sessionNote.sessionFocus}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className="session-note-content-section">
                            <Typography variant="h5" className="text-primary">
                                {t("sessionNotes.progressNotes")}
                            </Typography>
                            <Typography variant="body1">{sessionNote.progressNotes}</Typography>
                        </div>
                        <div className="session-note-content-section">
                            <Typography variant="h5" className="text-primary">
                                {t("sessionNotes.actionsItems")}
                            </Typography>
                            <Typography variant="body1">{sessionNote.actionsAgreed}</Typography>
                        </div>
                        <div className="session-note-content-section">
                            <Typography variant="h5" className="text-primary">
                                {t("sessionNotes.nextSessionFocus")}
                            </Typography>
                            <Typography variant="body1">{sessionNote.nextSessionFocus}</Typography>
                        </div>
                    </AccordionDetails>
                </Accordion>
                <div className="session-note-actions">
                    <Button
                        variant="text"
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="session-note-action"
                        startIcon={<DownloadIcon />}
                    >
                        {isDownloading ? t("sessionNotes.downloading") : t("sessionNotes.download")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SessionNote;
